import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { axiosInstance } from "../../services/v2/axiosInstance";
import { cursoCorto } from "../../utils/cursoCorto";
import useEsTelefono from "../../utils/esTelefono";
import "./buscadorHorarios.css";

/*
	Buscador de Horarios (compartido). Fuente: GET enrollmentUpdates/_schedules del plantel (2026-10-29), con las mismas
	reglas del Catálogo de Horarios (nombres de curso, horario leído del nombre, alumnas activas) más aula, cupo y estado.
	Antes bajaba el catálogo de TODOS los planteles y dos listas de 1,000 horarios, y tiraba lo que el catálogo no traía:
	los horarios Cerrando y los que terminaron sin cerrarse desaparecían. Alcance "vigentes" (lo abierto y lo que acaba de
	cerrar) o "historial" (además, todo horario que sale en una actualización). Búsqueda libre + filtros con casillas
	(Curso, Período, Día, Turno, Maestra, Aula, Estado) + vistas Lista / Por Maestra / Por Día.
*/

export interface HorarioFila {
	id: number;
	plantelId: number | null;
	curso: string;
	modulo: string;
	periodo: string;
	horario: string;
	/** 0 = lunes … 6 = domingo */
	dias: number[];
	/** Minuto del día en que empieza (-1 si no se sabe). */
	minuto: number;
	inicio: string;
	fin: string;
	maestro: string;
	aula: string | null;
	alumnos: number | null;
	cupo: number | null;
	/** Activo, Cerrando, Fusionado, Completado, Nunca comenzó. */
	status: string;
}

/** Lo que cada pantalla sabe de un horario destino y el catálogo no (sesión en la que entra, cupo, lleno). */
export interface DetalleHorario { cupo?: number | null; alumnos?: number | null; lleno?: boolean; sesion?: number | null; }

const DIAS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const TURNOS = ["Mañana", "Tarde", "Noche"];
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

const sinAcentos = (t: any): string => String(t || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

const periodoOrden = (p: string): number => {
	const t = sinAcentos(p);
	const year = (t.match(/(19|20)\d{2}/) || [""])[0];
	const mes = MESES.findIndex((m) => t.includes(sinAcentos(m)));
	return !year || mes < 0 ? -1 : parseInt(year, 10) * 12 + mes;
};

const turnoDe = (minuto: number): string => minuto < 0 ? "" : minuto < 12 * 60 ? TURNOS[0] : minuto < 18 * 60 ? TURNOS[1] : TURNOS[2];

const hoyIso = (): string => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

/** "Por Comenzar", "En Curso", "Finalizó" (Activo con la fecha de fin ya pasada) o el estado del horario cuando no está Activo. */
export const estadoHorario = (h: HorarioFila): string => {
	if (h.status && h.status !== "Activo") { return h.status === "Nunca comenzó" ? "Nunca Comenzó" : h.status; }
	if (h.fin && h.fin < hoyIso()) { return "Finalizó"; }
	return h.inicio && h.inicio > hoyIso() ? "Por Comenzar" : "En Curso";
};

/** Un horario destino que ya trae `_options` (TargetSchedule) como fila del buscador. */
export const filaDeDestino = (t: { id: number; name: string; levelName?: string; periodName?: string; mentorName: string | null; startDate: string | null; endDate: string | null;
	enrolled: number; limit: number; sessions: string | null; statusId?: number }, curso?: string, plantelId?: number | null): HorarioFila => ({
	id: t.id, plantelId: plantelId ?? null, curso: curso || "", modulo: t.levelName || "", periodo: t.periodName || "", horario: t.sessions || t.name, dias: [], minuto: -1,
	inicio: (t.startDate || "").slice(0, 10), fin: (t.endDate || "").slice(0, 10), maestro: t.mentorName || "", aula: null, alumnos: t.enrolled, cupo: t.limit || null,
	status: t.statusId === 4 ? "Cerrando" : "Activo"
});

// ---------------------------------------------------------------- datos

export type AlcanceHorarios = "vigentes" | "historial";
const vigencia = 60 * 1000;
const cache = new Map<string, { t: number; p: Promise<HorarioFila[]> }>();

const STATUS_POR_ID: { [id: number]: string } = { 0: "Nunca comenzó", 1: "Activo", 2: "Fusionado", 3: "Completado", 4: "Cerrando" };

const cargarFilas = (campusId: number, alcance: AlcanceHorarios): Promise<HorarioFila[]> => {
	const clave = campusId + ":" + alcance;
	const antes = cache.get(clave);
	if (antes && Date.now() - antes.t < vigencia) { return antes.p; }
	const p = axiosInstance.get<any>(`/admin/campus/${campusId}/enrollmentUpdates/_schedules?scope=${alcance}`).then((r) => (r.data?.horarios || []).map((h: any): HorarioFila => ({
		id: h.id, plantelId: h.plantel ?? campusId, curso: h.curso || "", modulo: h.modulo || "", periodo: h.periodo || "", horario: h.horario || "",
		dias: Array.isArray(h.dias) ? h.dias : [], minuto: typeof h.minuto === "number" ? h.minuto : -1, inicio: h.inicio || "", fin: h.fin || "", maestro: h.maestro || "",
		aula: h.aula || null, alumnos: typeof h.alumnos === "number" ? h.alumnos : null, cupo: h.cupo || null, status: h.status || STATUS_POR_ID[h.statusId] || "Activo"
	})));
	p.catch(() => cache.delete(clave));
	cache.set(clave, { t: Date.now(), p });
	return p;
};

/** Horarios del plantel para el buscador (null = cargando). Si falla, el motivo y `reintentar`. */
export const useHorarios = (campusId: number, alcance: AlcanceHorarios = "vigentes"): { filas: HorarioFila[] | null; error: string | null; reintentar: () => void } => {
	const [filas, setFilas] = useState<HorarioFila[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [intento, setIntento] = useState<number>(0);
	useEffect(() => {
		let vigente = true;
		setFilas(null); setError(null);
		cargarFilas(campusId, alcance).then((f) => vigente && setFilas(f))
			.catch((e) => vigente && (setError(e?.response?.data?.message || e?.message || "No se pudieron cargar los horarios."), setFilas([])));
		return () => { vigente = false; };
	}, [campusId, alcance, intento]);
	return { filas, error, reintentar: () => setIntento((n) => n + 1) };
};

// ---------------------------------------------------------------- filtros

type Claves = "curso" | "periodo" | "dia" | "turno" | "maestra" | "aula" | "estado";
type Elegidos = { [k in Claves]?: string[] };

const ETIQUETAS: { [k in Claves]: string } = { curso: "Curso", periodo: "Período", dia: "Día", turno: "Turno", maestra: "Maestra", aula: "Aula", estado: "Estado" };
const ORDEN: Claves[] = ["curso", "periodo", "dia", "turno", "maestra", "aula", "estado"];

const valoresDe = (h: HorarioFila, k: Claves): string[] => {
	switch (k) {
		case "curso": return [h.curso || cursoCorto(h.modulo)].filter(Boolean);
		case "periodo": return h.periodo ? [h.periodo] : [];
		case "dia": return h.dias.map((d) => DIAS[d]).filter(Boolean);
		case "turno": { const t = turnoDe(h.minuto); return t ? [t] : []; }
		case "maestra": return h.maestro ? [h.maestro] : [];
		case "aula": return h.aula ? [h.aula] : [];
		case "estado": return [estadoHorario(h)];
	}
};

export interface OpcionFiltro { v: string; t: string; }

/** Chip con lista de casillas y búsqueda (nunca un desplegable simple). `unico`: elegir uno reemplaza al anterior. */
export const FiltroCasillas: React.FC<{ etiqueta: string; opciones: OpcionFiltro[]; elegidos: string[]; onCambio: (v: string[]) => void; unico?: boolean; deshabilitado?: boolean }> =
	({ etiqueta, opciones, elegidos, onCambio, unico, deshabilitado }) => {
	const [abierto, setAbierto] = useState<boolean>(false);
	const [aguja, setAguja] = useState<string>("");
	const [corrimiento, setCorrimiento] = useState<number>(0);
	const raiz = useRef<HTMLDivElement>(null);
	const panel = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!abierto) { setAguja(""); return; }
		const fuera = (e: MouseEvent) => { if (raiz.current && !raiz.current.contains(e.target as Node)) { setAbierto(false); } };
		const tecla = (e: KeyboardEvent) => { if (e.key === "Escape") { setAbierto(false); } };
		document.addEventListener("mousedown", fuera);
		document.addEventListener("keydown", tecla);
		return () => { document.removeEventListener("mousedown", fuera); document.removeEventListener("keydown", tecla); };
	}, [abierto]);

	useLayoutEffect(() => {
		if (!abierto || !panel.current) { setCorrimiento(0); return; }
		const r = panel.current.getBoundingClientRect();
		const sobra = r.right - (window.innerWidth - 8);
		setCorrimiento(sobra > 0 ? -sobra : 0);
	}, [abierto]);

	const visibles = aguja ? opciones.filter((o) => sinAcentos(o.t).includes(sinAcentos(aguja))) : opciones;
	const alternar = (o: string) => {
		if (unico) { onCambio(elegidos.includes(o) ? [] : [o]); setAbierto(false); return; }
		onCambio(elegidos.includes(o) ? elegidos.filter((x) => x !== o) : [...elegidos, o]);
	};
	const texto = (v: string) => opciones.find((o) => o.v === v)?.t || v;
	return (
		<div className="wbh-filtro" ref={raiz}>
			<button type="button" className={"wbh-chip" + (elegidos.length ? " on" : "")} aria-haspopup="listbox" aria-expanded={abierto} disabled={deshabilitado} onClick={() => setAbierto(!abierto)}>
				{etiqueta}{elegidos.length === 1 ? <b>{texto(elegidos[0])}</b> : elegidos.length > 1 ? <b>{elegidos.length}</b> : <span className="caret" aria-hidden="true">▾</span>}
			</button>
			{elegidos.length ? <button type="button" className="wbh-x" aria-label={"Quitar " + etiqueta} onClick={() => onCambio([])}>×</button> : null}
			{abierto ? (
				<div className="wbh-pop" ref={panel} style={corrimiento ? { left: corrimiento } : undefined}>
					{opciones.length > 8 ? <input type="search" className="wbh-pop-buscar" autoFocus value={aguja} onChange={(e) => setAguja(e.target.value)} aria-label={"Buscar " + etiqueta} /> : null}
					{visibles.length === 0 ? <div className="wbh-vacio">Nada por aquí.</div> : visibles.map((o) => (
						<label key={o.v} className="wbh-op"><input type={unico ? "radio" : "checkbox"} checked={elegidos.includes(o.v)} onChange={() => alternar(o.v)} /><span>{o.t}</span></label>
					))}
				</div>
			) : null}
		</div>
	);
};

// ---------------------------------------------------------------- buscador

export interface BuscadorHorariosProps {
	campusId: number;
	/** Horario elegido (se resalta). */
	seleccionado?: number | null;
	onElegir: (h: HorarioFila) => void;
	/** Solo estos horarios (los destinos que el servidor permite). Los que el catálogo no trae salen de `complemento`. */
	soloIds?: number[];
	/** Orden sugerido (ids, el primero arriba); los que no vienen quedan al final en su orden. */
	orden?: number[];
	complemento?: HorarioFila[];
	detalle?: { [id: number]: DetalleHorario };
	/** Valores con los que arranca cada filtro (por ejemplo Curso de la matrícula). */
	inicial?: Elegidos;
	/** Tabla angosta para tarjetas de media pantalla. */
	compacto?: boolean;
	/** Estados que no se ofrecen (por ejemplo Completado para un destino). */
	ocultarEstados?: string[];
	placeholder?: string;
	autoFocus?: boolean;
	/** "historial": también los horarios viejos que salen en alguna actualización (filtro del Historial). */
	alcance?: AlcanceHorarios;
}

type Vista = "lista" | "maestra" | "dia";
const VISTAS: { k: Vista; t: string }[] = [{ k: "lista", t: "Lista" }, { k: "maestra", t: "Por Maestra" }, { k: "dia", t: "Por Día" }];

const BuscadorHorarios: React.FC<BuscadorHorariosProps> = ({ campusId, seleccionado, onElegir, soloIds, orden, complemento, detalle, inicial, compacto, ocultarEstados, placeholder, autoFocus, alcance }) => {
	const movil = useEsTelefono();
	const { filas, error, reintentar } = useHorarios(campusId, alcance || "vigentes");
	const [q, setQ] = useState<string>("");
	const [elegidos, setElegidos] = useState<Elegidos>(inicial || {});
	const [vista, setVista] = useState<Vista>("lista");

	const base: HorarioFila[] = useMemo(() => {
		if (!filas) { return []; }
		let lista = filas;
		if (soloIds) {
			const set = new Set(soloIds);
			lista = filas.filter((f) => set.has(f.id));
			const tengo = new Set(lista.map((f) => f.id));
			(complemento || []).forEach((c) => { if (set.has(c.id) && !tengo.has(c.id)) { lista.push(c); } });
			// Lo que dice el servidor del destino manda sobre el catálogo (cupo y alumnas al momento).
			lista = lista.map((f) => { const d = detalle?.[f.id]; return d ? { ...f, cupo: d.cupo ?? f.cupo, alumnos: d.alumnos ?? f.alumnos } : f; });
		}
		if (orden?.length) {
			const pos = new Map(orden.map((id, i) => [id, i] as [number, number]));
			const fin = orden.length;
			lista = lista.map((f, i) => ({ f, i })).sort((a, b) => (pos.get(a.f.id) ?? fin) - (pos.get(b.f.id) ?? fin) || a.i - b.i).map((x) => x.f);
		}
		return ocultarEstados?.length ? lista.filter((f) => !ocultarEstados.includes(f.status)) : lista;
	}, [filas, soloIds, orden, complemento, detalle, ocultarEstados]);

	const opciones = useMemo(() => {
		const out: { [k in Claves]: string[] } = { curso: [], periodo: [], dia: [], turno: [], maestra: [], aula: [], estado: [] };
		ORDEN.forEach((k) => {
			const set = new Set<string>();
			base.forEach((h) => valoresDe(h, k).forEach((v) => set.add(v)));
			let lista = Array.from(set);
			if (k === "dia") { lista = DIAS.filter((d) => set.has(d)); }
			else if (k === "turno") { lista = TURNOS.filter((d) => set.has(d)); }
			else if (k === "periodo") { lista.sort((a, b) => periodoOrden(b) - periodoOrden(a) || a.localeCompare(b)); }
			else { lista.sort((a, b) => a.localeCompare(b, "es", { numeric: true })); }
			out[k] = lista;
		});
		return out;
	}, [base]);

	const lista = useMemo(() => {
		const aguja = sinAcentos(q);
		return base.filter((h) => {
			if (aguja) {
				const pajar = sinAcentos([h.curso, h.modulo, h.periodo, h.horario, h.maestro, h.aula, h.status, estadoHorario(h)].join(" "));
				if (!aguja.split(/\s+/).every((p) => pajar.includes(p))) { return false; }
			}
			return ORDEN.every((k) => { const e = elegidos[k]; return !e || e.length === 0 || valoresDe(h, k).some((v) => e.includes(v)); });
		});
	}, [base, q, elegidos]);

	const hayFiltros = ORDEN.some((k) => (elegidos[k] || []).length > 0);
	const cupoTexto = (h: HorarioFila): string => {
		const d = detalle?.[h.id];
		const cupo = d?.cupo ?? h.cupo; const alumnos = d?.alumnos ?? h.alumnos;
		if (alumnos == null) { return "—"; }
		return cupo ? `${alumnos} / ${cupo}` : String(alumnos);
	};
	const pastilla = (h: HorarioFila) => {
		const e = estadoHorario(h);
		const clase = e === "Cerrando" || e === "Finalizó" ? "oro" : e === "Por Comenzar" ? "act" : "flat";
		return <span className={"wbh-pill " + clase}>{e}</span>;
	};
	const sesionTexto = (h: HorarioFila) => { const s = detalle?.[h.id]?.sesion; return s ? "Va en la sesión " + s : ""; };
	const lleno = (h: HorarioFila) => !!detalle?.[h.id]?.lleno;

	const grupos: { titulo: string; filas: HorarioFila[] }[] = useMemo(() => {
		if (vista === "lista") { return [{ titulo: "", filas: lista }]; }
		const mapa = new Map<string, HorarioFila[]>();
		lista.forEach((h) => {
			const claves = vista === "maestra" ? [h.maestro || "Sin Maestra"] : (h.dias.length ? h.dias.map((d) => DIAS[d]) : ["Sin Día"]);
			claves.forEach((c) => mapa.set(c, [...(mapa.get(c) || []), h]));
		});
		const claves = Array.from(mapa.keys());
		if (vista === "dia") { claves.sort((a, b) => (DIAS.indexOf(a) + 1 || 99) - (DIAS.indexOf(b) + 1 || 99)); }
		else { claves.sort((a, b) => a.localeCompare(b, "es")); }
		return claves.map((c) => ({ titulo: c, filas: mapa.get(c) || [] }));
	}, [lista, vista]);

	const hayAula = opciones.aula.length > 0;
	const elegir = (h: HorarioFila) => onElegir(h);
	const onTecla = (e: React.KeyboardEvent, h: HorarioFila) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); elegir(h); } };

	const fila = (h: HorarioFila, ocultarMaestra: boolean) => (
		<tr key={"h" + h.id} className={"wbh-fila" + (seleccionado === h.id ? " sel" : "")} tabIndex={0} role="button" aria-pressed={seleccionado === h.id}
			onClick={() => elegir(h)} onKeyDown={(e) => onTecla(e, h)}>
			{/* Compacto no tiene columna Período: va bajo el horario, para no confundir Sábados de Agosto con Sábados de Octubre. */}
			<td><b>{h.horario || "—"}</b>{(compacto && h.periodo) || sesionTexto(h) ? <div className="wbh-sub">{[compacto ? h.periodo : "", sesionTexto(h)].filter(Boolean).join(", ")}</div> : null}</td>
			<td>{h.modulo || h.curso}</td>
			{ocultarMaestra ? null : <td>{h.maestro || "—"}</td>}
			{compacto ? null : <td>{h.periodo || "—"}</td>}
			{compacto || !hayAula ? null : <td>{h.aula || "—"}</td>}
			<td className="num">{cupoTexto(h)}{lleno(h) ? <span className="wbh-pill oro">Lleno</span> : null}</td>
			{compacto ? null : <td>{pastilla(h)}</td>}
		</tr>
	);
	const encabezado = (ocultarMaestra: boolean) => (
		<thead><tr><th>Horario</th><th>Módulo</th>{ocultarMaestra ? null : <th>Maestra</th>}{compacto ? null : <th>Período</th>}{compacto || !hayAula ? null : <th>Aula</th>}<th>Alumnas</th>{compacto ? null : <th>Estado</th>}</tr></thead>
	);
	const tarjeta = (h: HorarioFila) => (
		<button type="button" key={"t" + h.id} className={"wbh-tarjeta" + (seleccionado === h.id ? " sel" : "")} aria-pressed={seleccionado === h.id} onClick={() => elegir(h)}>
			<span className="wbh-tarjeta-linea"><b>{h.horario || "—"}</b>{pastilla(h)}</span>
			<span>{h.modulo || h.curso}{h.maestro ? ", " + h.maestro : ""}</span>
			<span className="wbh-sub">{h.periodo}{h.aula ? ", " + h.aula : ""}{h.periodo || h.aula ? ", " : ""}{cupoTexto(h)} alumnas{sesionTexto(h) ? ", " + sesionTexto(h) : ""}{lleno(h) ? ", Lleno" : ""}</span>
		</button>
	);

	const claveFiltros = ORDEN.filter((k) => opciones[k].length > 0 || (elegidos[k] || []).length > 0);
	return (
		<div className={"wbh" + (compacto ? " compacto" : "")}>
			<div className="wbh-buscar">
				<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
				<input type="search" value={q} autoFocus={autoFocus} placeholder={placeholder || "Curso, horario, maestra"} aria-label="Buscar horario" onChange={(e) => setQ(e.target.value)} />
				{filas ? <span className="wbh-cuenta">{lista.length === 1 ? "1 horario" : `${lista.length} horarios`}</span> : null}
			</div>
			<div className="wbh-chips">
				{claveFiltros.map((k) => (
					<FiltroCasillas key={k} etiqueta={ETIQUETAS[k]} opciones={opciones[k].map((v) => ({ v, t: v }))} elegidos={elegidos[k] || []} onCambio={(v) => setElegidos({ ...elegidos, [k]: v })} />
				))}
				{hayFiltros ? <button type="button" className="wbh-limpiar" onClick={() => setElegidos({})}>Quitar Filtros</button> : null}
				<span className="wbh-seg" role="group" aria-label="Vista">
					{VISTAS.map((v) => <button key={v.k} type="button" className={vista === v.k ? "on" : ""} aria-pressed={vista === v.k} onClick={() => setVista(v.k)}>{v.t}</button>)}
				</span>
			</div>
			{error ? <div className="wbh-error" role="alert">{error} <button type="button" className="wbh-limpiar" onClick={reintentar}>Reintentar</button></div> : null}
			<div className="wbh-lista">
				{filas === null ? <div className="wbh-vacio">Cargando…</div> : lista.length === 0 ? <div className="wbh-vacio">Nada por aquí.</div> : grupos.map((g) => (
					<div key={g.titulo || "todo"} className="wbh-grupo">
						{g.titulo ? <div className="wbh-grupo-t">{g.titulo}<span>{g.filas.length}</span></div> : null}
						{movil ? <div className="wbh-tarjetas">{g.filas.map(tarjeta)}</div> : (
							<table className="wbh-tabla">{encabezado(vista === "maestra")}<tbody>{g.filas.map((h) => fila(h, vista === "maestra"))}</tbody></table>
						)}
					</div>
				))}
			</div>
		</div>
	);
};

export default BuscadorHorarios;
